import React, { useState } from 'react';
import { Text, View, StyleSheet, ScrollView } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';
import Pdf from 'react-native-pdf';

import Image from '@components/common/ImageOverlay';
import EditableLine from '@components/common/editableLine';
import TouchableElement from '@components/common/touchableElement';
import Preview from '@components/imageHandling/components/thumbnails';
import { SmallLoading } from '@components/loading';

import { scale } from '@components/frame';
import { isPhone, Alert, ImageUpload, toBase64, isWeb } from '@helpers';

import ColorWrapper from '@styles/colors';
import Svg from '@styles/icons/svg';
import { getBoxShadowStyle } from '@styles/shadow';
import { font } from '@styles/fonts';

import { applyPartMasterDataChanges } from '@reducers/admin';
import { addImage } from '@reducers/imageHandling';
import { startQueue } from '@reducers/uploadQueue';
import { KEYS_TAGS } from '@components/imageHandling/common';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

export const partFields = [
  {
    name: 'Part',
    noArrow: true,
    key: 'partName',
    small: false,
    placeholder: 'Part Number',
    maxLength: 40,
  },
  {
    name: 'Drawing',
    noArrow: true,
    key: 'drawing',
    small: false,
    placeholder: 'Drawing Number',
    maxLength: 40,
  },
  {
    name: 'Revision',
    noArrow: true,
    key: 'revision',
    small: false,
    placeholder: 'Revision',
    maxLength: 40,
  },
  {
    name: 'Pos',
    noArrow: true,
    key: 'position',
    small: false,
    placeholder: 'Pos',
    maxLength: 10,
  },
  {
    name: 'Description',
    noArrow: true,
    small: false,
    key: 'description',
    placeholder: 'Description',
    maxLength: 120,
  },
];

const ImagesRender = ({ images, C }) => {
  return images.map((i, index) => (
    <View
      key={`${i.id}-image-${index}`}
      style={[
        {
          width: scale(123),
          height: scale(83),
          justifyContent: 'center',
          alignContent: 'center',
          alignItems: 'center',
          marginRight: scale(4),
        },
      ]}
    >
      {i.isPreview || i.isDocument ? (
        <Preview machineList={true} fontName="Subhead 2" item={i} />
      ) : i.type === 'application/pdf' || (i.file && i.file.type === 'application/pdf') ? (
        <Pdf source={{ uri: i.urlSmall || i.uri }} style={{ width: '100%', height: '100%' }} />
      ) : (
        <Image
          {...{
            source: { uri: i.base64 || i.urlSmall || i.uri },
            rotate: i?.customRotate?.[i?.page] || 0,
            style: { width: '100%', height: '100%' },
            resizeMode: 'contain',
            C,
          }}
        />
      )}
    </View>
  ));
};

const AddPart = ({ C, hide, applyPartMasterDataChanges, addImage, startQueue }) => {
  const [data, setData] = useState({});
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);

  const noImages = !images.length;

  const updateData = (key, value) => {
    const newData = Object.assign({}, data);
    newData[key] = value;
    setData(newData);
    if (!newData.drawing && !newData.part) {
      setSaveDisabled(true);
    } else {
      setSaveDisabled(false);
    }
  };

  const add = async (tags, image) => {
    var photo = isWeb
      ? image.file
      : {
          uri: image.uri,
          type: image.type,
          name: image.fileName || image.name,
        };

    const id = `TEMPIMAGEID-${Math.random().toString(36).substring(7)}`;
    await addImage(
      {
        photo,
        tags,
        tempID: id,
        isPDF: photo.type.includes('pdf'),
      },
      false
    );
  };

  const submit = async () => {
    setLoading(true);
    const result = await applyPartMasterDataChanges({ part: data });
    if (result?.isSuccess) {
      const tags = [
        {
          scopeId: KEYS_TAGS.Part,
          scopeReferenceId: result.part.partId,
          isFavorite: false,
        },
      ];
      for (const image of images) {
        if (!image.fileName) {
          continue;
        }

        await add(tags, image);
      }
      startQueue();
      hide();
      Alert({ title: 'Success' });
    } else {
      Alert({ title: result?.errorMessage || 'Error' });
    }

    setLoading(false);
  };

  return (
    <View>
      <View
        style={{
          width: '100%',
          zIndex: 1000,
          height: scale(58),
        }}
      >
        <View style={{ position: 'absolute', zIndex: 1000, left: 0 }}>
          <TouchableElement
            onPress={() => {
              hide();
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { padding: scale(15), flexDirection: 'row' }]}>
                <Text style={[{ color: C(pressHoverColor('System/Light/Blue')), marginLeft: scale(7) }, font.Body]}>{i18n.t('Cancel')}</Text>
              </View>
            )}
          </TouchableElement>
        </View>
        <View style={{ position: 'absolute', zIndex: 1000, right: 0 }}>
          <TouchableElement
            onPress={() => {
              if (loading || saveDisabled) {
                return;
              }
              submit();
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { padding: scale(15) }]}>
                {loading ? (
                  <SmallLoading />
                ) : (
                  <Text style={[font.Headline, { color: C(pressHoverColor(saveDisabled ? 'Label/Light/Quinary' : 'System/Light/Blue')) }]}>
                    {i18n.t('Save')}
                  </Text>
                )}
              </View>
            )}
          </TouchableElement>
        </View>
      </View>
      <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
        <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t('Add Part')}</Text>
      </View>
      <View style={{ paddingHorizontal: isPhone ? 0 : scale(16) }}>
        <View
          style={{
            marginBottom: scale(noImages ? 20 : 0),
          }}
        >
          <ScrollView
            horizontal={true}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ minWidth: '100%' }}
          >
            <View
              style={[
                {
                  width: '100%',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  height: scale(noImages ? 42 : 98),
                },
                !noImages && {
                  backgroundColor: C('Background/Light/Field'),
                },
                !noImages &&
                  !isPhone && {
                    borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
                    borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
                    borderBottomColor: C('Hairline / Light / No trans'),
                    borderBottomWidth: StyleSheet.hairlineWidth,
                  },
              ]}
            >
              <ImagesRender {...{ images, C }} />
              <TouchableElement
                onPress={() => {
                  ImageUpload({
                    headerText: 'Add to Part',
                    withoutAddExisting: true,
                    onlySelectImage: true,
                    uploadStart: async (image) => {
                      if (isWeb) {
                        if (!image || !image.name) {
                          return;
                        }

                        const uri = await toBase64(image);

                        setImages([...images, { file: image, id: image.name, uri, fileName: image.name }]);
                      } else {
                        if (image?.name) {
                          image.fileName = image.name;
                        }

                        if (!image || !image.fileName) {
                          return;
                        }
                        setImages([...images, { ...image, id: image.fileName }]);
                      }
                    },
                    tags: [],
                  });
                }}
              >
                {({ pressHoverColor, boxStyle }) => (
                  <View
                    style={[
                      boxStyle,
                      {
                        width: noImages ? '100%' : scale(123),
                        height: scale(noImages ? 42 : 83),
                        justifyContent: 'center',
                        alignContent: 'center',
                        alignItems: 'center',
                        backgroundColor: C(pressHoverColor(noImages ? 'Background/Light/Field' : 'Button/Light/Tertiary')),
                        marginRight: scale(noImages ? 0 : 16),
                        marginBottom: scale(0),
                        borderRadius: scale(noImages && !isPhone ? 9 : 0),
                      },
                    ]}
                  >
                    {noImages ? (
                      <Text style={[font['Button 1'], { color: C('Link/Light/Primary') }]}>{i18n.t('Add Part Image')}</Text>
                    ) : (
                      <Svg icon="addPicture" color={pressHoverColor('Button/Light/Primary')} width={scale(38)} height={scale(28)} key={scale(21)} />
                    )}
                  </View>
                )}
              </TouchableElement>
            </View>
          </ScrollView>
        </View>
        <View>
          <View
            style={[
              noImages && getBoxShadowStyle({ type: 'Field', C }),
              noImages && { borderRadius: scale(SCREEN_BORDER_RADIUS), zIndex: 1000 },
              isPhone && {
                borderTopWidth: StyleSheet.hairlineWidth,
                borderTopColor: C('Hairline/Light/No trans'),
                borderBottomWidth: StyleSheet.hairlineWidth,
                borderBottomColor: C('Hairline/Light/No trans'),
              },
            ]}
          >
            {partFields.map((item, index) => {
              return (
                <EditableLine
                  {...{
                    item: {
                      value: data[item.key],
                      onChange: (v) => {
                        updateData(item.key, v);
                      },
                      clearButtonMode: 'never',
                      ...item,
                    },
                    index,
                    array: partFields,
                    C,
                    bottomBorderBox: false,
                    topBorder: index !== 0,
                    roundTop: isPhone || !noImages ? false : index == 0,
                    roundBottom: isPhone ? false : index === partFields.length - 1,
                  }}
                  key={item.name}
                />
              );
            })}
          </View>
          <View>
            <Text
              style={[
                font['Caption 1'],
                { color: C('Label/Light/Tertiary'), paddingHorizontal: scale(16), paddingBottom: scale(16), marginTop: scale(6) },
              ]}
            >
              {i18n.t('If you add Part Information, either the field Part or Drawing must be filled')}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

export default connect(({}) => ({}), { applyPartMasterDataChanges, addImage, startQueue })(ColorWrapper(AddPart));
